import { useState, useEffect } from 'react';
import { Modal, ModalFooter } from './Modal';
import { Input } from './FormField';
import { Button } from '../ui/Button';
import { ErrorDisplay } from '../ui/ErrorDisplay';
import { ColorPicker } from './ColorPicker';
import { statusApi } from '../../services/api';

interface Status {
  id: string;
  name: string;
  color: string;
}

interface StatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  status?: Status | null;
  onSuccess: () => void;
}

export function StatusModal({
  isOpen,
  onClose,
  status,
  onSuccess,
}: StatusModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    color: '#3B82F6',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<Error | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditMode = Boolean(status);

  // Populate form when editing
  useEffect(() => {
    if (status) {
      setFormData({
        name: status.name || '',
        color: status.color || '#3B82F6',
      });
    } else {
      setFormData({
        name: '',
        color: '#3B82F6',
      });
    }
    setErrors({});
    setSubmitError(null);
  }, [status, isOpen]);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    } else if (formData.name.trim().length > 50) {
      newErrors.name = 'Name must be 50 characters or less';
    }

    if (!formData.color || !/^#[0-9A-F]{6}$/i.test(formData.color)) {
      newErrors.color = 'Valid color is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const data = {
        name: formData.name.trim(),
        color: formData.color,
      };

      if (isEditMode && status) {
        await statusApi.update(status.id, data);
      } else {
        await statusApi.create(data);
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to save status:', error);
      setSubmitError(error instanceof Error ? error : new Error('Failed to save status'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!status) return;

    if (!confirm(`Are you sure you want to delete "${status.name}"?`)) {
      return;
    }

    setIsSubmitting(true);

    try {
      await statusApi.delete(status.id);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to delete status:', error);
      setSubmitError(error instanceof Error ? error : new Error('Failed to delete status'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'Edit status' : 'Add new status'}
      size="small"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Name"
          name="name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="In progress"
          required
          error={errors.name}
        />

        <ColorPicker
          label="Color"
          value={formData.color}
          onChange={(color) => setFormData({ ...formData, color })}
          required
          error={errors.color}
        />

        <div className="rounded-md bg-surface-subtle border border-border-subtle p-3">
          <div className="text-sm font-medium text-text-primary mb-2">
            Preview
          </div>
          <div className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: formData.color }}
            />
            <span className="text-sm text-text-primary">
              {formData.name || 'Status name'}
            </span>
          </div>
        </div>

        <ErrorDisplay error={submitError} />

        <ModalFooter>
          {isEditMode && (
            <Button
              type="button"
              variant="danger"
              onClick={handleDelete}
              disabled={isSubmitting}
              className="mr-auto"
            >
              Delete
            </Button>
          )}
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : isEditMode ? 'Save changes' : 'Add status'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
